import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Box, HStack, Input, InputGroup, RadioGroup, Spacer, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { BaseButton, BaseButtonText, BaseDialog, BaseEmptyState, BaseIcon, BaseTooltip, useAuth } from '@cks-internal/react-base';
import { faArrowLeftFromBracket, faCircle, faCircleCheck, faMagnifyingGlass, faUsersSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { LoadingSpinner } from 'src/features/misc/LoadingSpinner';
import { useOnlineBookingFrontierUserContactsQuery } from 'src/globalModels/api/useOnlineBookingFrontierUserContactsQuery';
import { useProfileStore } from 'src/features/stores/useProfileStore';
import { useHotelBookingStore } from 'src/features/stores/useHotelBookingStore';
import { ApplicationRoutes } from 'src/constants/applicationRoutes';
import { FrontierUser } from 'src/globalModels/frontierUser';
import { ObtFrontierUserContactModel } from 'src/globalModels/ObtFrontierUserContactModel';

const minFilterCharacters = 3;

export const ProfileSelector = () => {
  const auth = useAuth<FrontierUser>();
  const query = useOnlineBookingFrontierUserContactsQuery();

  const setHotelSearchState = useHotelBookingStore((state) => state.setSearchQuery);
  const { users, selectedUserId, setUsers, setSelectedUserId } = useProfileStore();

  // Controlled input should never be undefined.
  const [findInPageText, setFindInPageText] = useState<string>('');

  // Keep a temp selection while dialog is open.
  const [tempSelectedUserId, setTempSelectedUserId] = useState<number | undefined>(undefined);

  const disclosure = useDisclosure({
    onClose: () => {
      setFindInPageText('');
      setTempSelectedUserId(undefined);
    }
  });

  const onLogoutClick = () => {
    localStorage.removeItem('selectedContactId');
    auth.logout().then(() => {
      document.cookie = 'frontier.auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      window.location.href = '/';
    });
  };

  const onChooseContactClick = () => {
    if (tempSelectedUserId !== undefined) {
      setSelectedUserId(tempSelectedUserId);
      localStorage.setItem('selectedContactId', tempSelectedUserId.toString());
      setHotelSearchState(undefined);
      disclosure.onClose();

      // Force re-fetch nav logo with an old-school redirect.
      window.location.href = ApplicationRoutes.booking.hotelSearchLanding;
    }
  };

  const didInitRef = useRef(false);

  useEffect(() => {
    if (!query.isFetched || !query.data) return;

    // Prevent loops / repeated hydration
    if (didInitRef.current) return;
    didInitRef.current = true;

    setUsers(query.data);

    const storedContactId = Number(localStorage.getItem('selectedContactId'));
    const foundContact = query.data.find((u) => u.ContactId === storedContactId);

    if (storedContactId && foundContact) {
      setSelectedUserId(storedContactId);
    } else {
      disclosure.onOpen();
    }
  }, [query.isFetched, query.data, setUsers, setSelectedUserId, disclosure]);


  const selectedUser = users?.find((u) => u.ContactId === selectedUserId);

  // Always derive filtered list from "users" so it updates immediately when users change.
  const filteredUsers = useMemo<ObtFrontierUserContactModel[]>(() => {
    const base = users ?? [];
    const text = findInPageText.trim().toLowerCase();

    if (text.length < minFilterCharacters) return base;

    return base.filter((u) => (u.CompanyName ?? '').toLowerCase().includes(text));
  }, [users, findInPageText]);

  // Avoid mutating arrays during render.
  const sortedFilteredUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => (a.CompanyName ?? '').localeCompare(b.CompanyName ?? ''));
  }, [filteredUsers]);

  const isFiltering = findInPageText.trim().length >= minFilterCharacters;
  const showNoProfilesForFilter = isFiltering && sortedFilteredUsers.length === 0;
  const showNoProfilesAtAll =
    query.isFetched && !query.isError && !query.isFetching && (users?.length ?? 0) === 0;
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!disclosure.open) return;
    if (!selectedUserId) return;

    // Wait for dialog + list to render
    requestAnimationFrame(() => {
      itemRefs.current[selectedUserId]?.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
    });
  }, [disclosure.open, selectedUserId]);

  return (
    <>
      <BaseTooltip content='Profile Selector'>
        <Avatar.Root
          variant='outline'
          onClick={() => {
            // Sync temp selection on open so user sees current selection in dialog.
            setTempSelectedUserId(selectedUserId);
            disclosure.onOpen();
          }}
          cursor='pointer'
          backgroundColor='primary'
          color='white'>
          <Avatar.Fallback name={selectedUser?.Name || '---'} />
        </Avatar.Root>
      </BaseTooltip>

      <BaseDialog
        placement='top'
        disableCloseOnClick={false}
        closeOnEsc={true}
        hideCloseButton={false}
        disclosure={disclosure}
        isLoading={false}
        header='Select Profile'
        footerRender={
          <HStack width='full'>
            <BaseButton
              icon={faArrowLeftFromBracket}
              variant='outline'
              onClick={onLogoutClick}
              color='red'
              borderColor='red'
              buttonText={BaseButtonText.LOGOUT}
            />
            <Spacer />
            <BaseButton
              disabled={(users?.length ?? 0) === 0 || tempSelectedUserId === undefined}
              onClick={onChooseContactClick}
              buttonText={BaseButtonText.APPLY}
            />
          </HStack>
        }
        headerSeparator={{ divideColor: '#ebebeb', divideWidth: '1px' }}
        footerSeparator={{ divideColor: '#ebebeb', divideWidth: '1px' }}
        size='sm'>
        <Box position='sticky' top='0' zIndex='sticky'>
          <Box flex='1'>
            <InputGroup
              startElement={<BaseIcon icon={faMagnifyingGlass} color='grey' />}
              endElement={
                findInPageText.length > 0 ? (
                  <BaseIcon
                    icon={faClose}
                    color='grey'
                    onClick={() => {
                      setFindInPageText('');
                    }}
                  />
                ) : null
              }>
              <Input
                value={findInPageText}
                onChange={(e) => setFindInPageText(e.target.value.toLowerCase())}
                variant={'flushed'}
                color={'black'}
                borderRadius={'8px'}
                placeholder={`Find Company${minFilterCharacters ? ` (min ${minFilterCharacters} chars)` : ''}`}
                css={{
                  bg: 'gray.100',
                  _hover: {
                    bg: 'gray.200'
                  },
                  _focus: {
                    bg: 'gray.100',
                    borderColor: 'primary.500'
                  }
                }}
              />
            </InputGroup>
          </Box>
        </Box>

        <RadioGroup.Root
          name='user'
          value={tempSelectedUserId?.toString()}
          onValueChange={({ value }) => setTempSelectedUserId(Number(value))}
          marginTop={'16px'}>
          {query.isFetching && <LoadingSpinner />}

          {showNoProfilesAtAll && (
            <BaseEmptyState
              title='No Profiles Found'
              description='There are no Company Contacts associated with this account.'
              icon={<FontAwesomeIcon icon={faUsersSlash} />}
            />
          )}

          {showNoProfilesForFilter && (
            <BaseEmptyState
              title='No Profiles Found'
              description={`There are no Company names matching "${findInPageText}".`}
              icon={<FontAwesomeIcon icon={faUsersSlash} />}
            />
          )}

          {sortedFilteredUsers.length > 0 && (
            <VStack gap='8px' align='stretch'>
              {sortedFilteredUsers.map((user) => (
                <Box
                  key={user.ContactId}
                  ref={(el: HTMLDivElement | null) => {
                    itemRefs.current[user.ContactId] = el;
                  }}
                  border={user.ContactId === tempSelectedUserId ? '1px dashed lightgray' : '1px solid transparent'}
                  borderRadius='md'
                  padding='8px'>
                  <RadioGroup.Item value={user.ContactId.toString()} cursor='pointer' width='100%'>
                    <HStack gap='16px' width='100%'>
                      <Avatar.Root
                        variant={user.ContactId === tempSelectedUserId ? 'subtle' : 'outline'}
                        backgroundColor={user.ContactId === tempSelectedUserId ? 'primary' : 'white'}
                        color={user.ContactId === tempSelectedUserId ? 'white' : 'black'}>
                        <Avatar.Fallback
                          fontWeight={user.ContactId === tempSelectedUserId ? 'bold' : 'medium'}
                          name={user.Name}
                        />
                      </Avatar.Root>

                      <VStack gap='0' align='left' fontWeight={user.ContactId === selectedUserId ? 'bold' : 'medium'}>
                        <Text fontSize='md' fontWeight={'bold'}>
                          {user.CompanyName}
                        </Text>
                        <Text color='fg.muted' fontSize='xs'>
                          {user.Name}
                        </Text>
                        <Text color='fg.muted' fontSize='xs'>
                          {user.FrontierUserEmail}
                        </Text>
                      </VStack>

                      <Spacer />

                      <FontAwesomeIcon
                        icon={user.ContactId === tempSelectedUserId ? faCircleCheck : faCircle}
                        size='2x'
                        color={user.ContactId === tempSelectedUserId ? 'primary' : 'lightgrey'}
                      />
                    </HStack>

                    <RadioGroup.ItemHiddenInput />
                  </RadioGroup.Item>
                </Box>
              ))}
            </VStack>
          )}
        </RadioGroup.Root>
      </BaseDialog>
    </>
  );
};
